/**
 * Marker DOM and visual state renderer.
 *
 * Why this file exists:
 * - Separates rendering concerns from lifecycle/orchestration code.
 * - Ensures marker element updates are minimal and consistent.
 *
 * Responsibilities:
 * - Build marker elements.
 * - Bind domain data into marker element datasets/attributes.
 * - Apply visual modes (pin/price/custom) with minimal DOM writes.
 * - Reset visual state before pooled reuse.
 */
import { isSameMarkerPosition } from './utils';

function defaultResolveElementClassName(markerData) {
  if (markerData?.isMasterPlannedCommunity) {
    return 'nhs-c-marker nhs-c-marker--mpc';
  }

  if (markerData?.isAdult) {
    return 'nhs-c-marker nhs-c-marker--adult';
  }

  return 'nhs-c-marker';
}

export default function createMarkerRenderer({
  resolveElementClassName = defaultResolveElementClassName,
} = {}) {

  function createMarkerElement(markerData = null) {
    const element = document.createElement('div');
    element.className = resolveElementClassName(markerData);
    element.dataset.markerIcon = 'true';

    return element;
  }

  function bindElementData(record, markerData) {
    const id = markerData.id;
    const communityId = String(markerData.communityId) ?? '';
    const isBasic = String(markerData.isBasic) ?? '';

    if (record.element.dataset.id !== id) {
      record.element.dataset.id = id;
    }

    if (record.element.dataset.communityId !== communityId) {
      record.element.dataset.communityId = communityId;
    }

    if (record.element.dataset.isBasic !== isBasic) {
      record.element.dataset.isBasic = isBasic;
    }

    const type = String(markerData.type) ?? '';
    if (record.element.dataset.type !== type) {
      record.element.dataset.type = type;
    }

    const nextBaseClassName = resolveElementClassName(markerData);
    const hasPriceClass = record.element.classList.contains('nhs-map-pin--price');
    const nextClassName = hasPriceClass ? `${nextBaseClassName} nhs-map-pin--price` : nextBaseClassName;
    if (record.element.className !== nextClassName) {
      record.element.className = nextClassName;
    }
  }

  function bindMarkerData(record, markerData) {
    if (!isSameMarkerPosition(record.marker.position, markerData.position)) {
      record.marker.position = markerData.position;
    }

    if (record.marker.title !== markerData.title) {
      record.marker.title = markerData.title;
    }

    if (record.marker.zIndex !== markerData.zIndex) {
      record.marker.zIndex = markerData.zIndex;
    }
  }

  function bindRecord(record, markerData) {
    record.markerData = markerData;
    record.mode = null;
    bindElementData(record, markerData);
    bindMarkerData(record, markerData);
  }

  function canApplyMode(record) {
    return record.mode !== 'custom';
  }

  function applyModeClass(record, mode) {
    const isPriceMode = mode === 'price';
    const modeChanged = record.mode !== mode;

    if (modeChanged) {
      record.mode = mode;
      record.element.classList.toggle('nhs-map-pin--price', isPriceMode);
    }

    return { isPriceMode, modeChanged };
  }

  function applyModeContent(record, isPriceMode, modeChanged) {
    if (isPriceMode) {
      const currentPrice = record.markerData.displayText || '';
      if (record.lastPrice !== currentPrice) {
        record.element.textContent = currentPrice;
        record.lastPrice = currentPrice;
      }
      return;
    }

    if (modeChanged && record.element.textContent !== '') {
      record.element.textContent = '';
      record.lastPrice = '';
    }
  }

  function applyMode(record, mode) {
    const { isPriceMode, modeChanged } = applyModeClass(record, mode);
    applyModeContent(record, isPriceMode, modeChanged);
  }

  function resetRecord(record) {
    record.markerData = null;
    record.mode = null;
    record.lastPrice = '';
    record.spatialKey = null;
    record.element.classList.remove('nhs-map-pin--price');
    record.element.textContent = '';
  }

  function setMarkerVisible(record, map, visible) {
    const nextMap = visible ? map : null;

    if (record.marker.map !== nextMap) {
      record.marker.map = nextMap;
    }
  }

  function resolveMode(markerData, behaviorContext, resolveMarkerMode) {
    return resolveMarkerMode?.(markerData, behaviorContext) || markerData.renderMode || 'pin';
  }

  return {
    createMarkerElement,
    bindRecord,
    canApplyMode,
    applyMode,
    resetRecord,
    setMarkerVisible,
    resolveMode,
  };
}
